// Spawn points for joining players: slots on an arc facing the speaker wall, 0.8 m apart, filled from the
// middle outwards (0, +1, -1, +2, -2, ...), then a second and third row 0.8 m closer to the wall. The first
// slot is the manifest spawn. A slot is used if it is inside the walkable area and at least MIN_GAP from
// every other player (including slots the server is holding for players who are reconnecting).

import type { WalkCollider } from './collision';

const SPACING = 0.8;
const RADIUS = 3.2;      // arc radius: the arc's centre is this far in front of the manifest spawn
const PER_ROW = 7;
const ROWS = 3;
const MIN_GAP = 0.7;

export interface SpawnPoint { x: number; z: number; yaw: number }

/** Slot positions in fill order. yaw 0 looks down -Z; each slot faces the arc's centre. */
export function spawnSlots(x0: number, z0: number, yaw0: number): SpawnPoint[] {
  const fx = -Math.sin(yaw0), fz = -Math.cos(yaw0); // forward
  const cx = x0 + fx * RADIUS, cz = z0 + fz * RADIUS;
  const out: SpawnPoint[] = [];
  for (let row = 0; row < ROWS; row++) {
    const r = RADIUS - row * SPACING;
    for (let i = 0; i < PER_ROW; i++) {
      const k = i === 0 ? 0 : (i % 2 ? 1 : -1) * Math.ceil(i / 2);
      const a = (k * SPACING) / r;
      // rotate the backward vector (-f) by a about +Y
      const bx = -fx, bz = -fz;
      const dx = bx * Math.cos(a) + bz * Math.sin(a);
      const dz = -bx * Math.sin(a) + bz * Math.cos(a);
      const x = cx + dx * r, z = cz + dz * r;
      out.push({ x, z, yaw: Math.atan2(-(cx - x), -(cz - z)) });
    }
  }
  return out;
}

/** First free slot, or null when every slot is taken or outside the room. */
export function pickSpawn(collider: WalkCollider, x0: number, z0: number, yaw0: number, others: readonly { x: number; z: number }[]): SpawnPoint | null {
  for (const s of spawnSlots(x0, z0, yaw0)) {
    collider.settle(s.x, s.z);
    const x = collider.x, z = collider.z;
    if (Math.hypot(x - s.x, z - s.z) > 0.3) continue; // too close to a wall
    if (others.some((o) => Math.hypot(o.x - x, o.z - z) < MIN_GAP)) continue;
    return { x, z, yaw: s.yaw };
  }
  return null;
}
